import React, { useState, useMemo } from "react";
import VisitorCard from "../../../../widgets/VisitorCard";
import {
  Autocomplete,
  Box,
  Card,
  Chip,
  Grid,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import BadgeIcon from "@mui/icons-material/Badge";
import PageHeader from "../../common/PageHeader";
import CustomCircularP from "../../../../widgets/CustomCircularP";
import FilterDialogMui from "../../../../widgets/FilterDialogMui";
import AlphabetSortWidget from "./AlphabetSortWidget";
import useProductCategories from "../../visitor/registration/hooks/useProductCategories";
import useApiEp from "../../../../hooks/useApiEp";

const Visitors = ({ type }) => {
  // --- States ---
  const [page, setPage] = useState(1);
  const [sq, setSq] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [openFilter, setOpenFilter] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");

  // Filter States
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [designation, setDesignation] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  // --- API Fetches ---
  const { data: Mdata } = useProductCategories();
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-visitors?type=${
      type ?? ""
    }&page=${page}&sq=${sq}&category=${selectedCategories.join(
      ","
    )}&country=${selectedCountry}&letter=${selectedLetter}&designation=${encodeURIComponent(
      designation || ""
    )}`
  );

  // Extract Category options (A01.00-Turning Machines, etc.)
  const uniqueCategories = useMemo(() => {
    if (!Mdata?.categories) return [];
    return [
      ...new Set(
        Mdata.categories
          .map((item) => item.Category || item.MainCategory || item.SubCategory)
          .filter(Boolean)
      ),
    ].sort();
  }, [Mdata]);

  // Dynamic Designation options list
  const designationOptions = useMemo(() => {
    const list = new Set([
      "CEO",
      "Director",
      "Manager",
      "Engineer",
      "Consultant",
      "Managing Director",
      "General Manager",
      "Purchase Manager",
      "Proprietor",
      "Partner",
    ]);

    if (data?.data && Array.isArray(data.data)) {
      data.data.forEach((v) => {
        if (v?.designation) list.add(v.designation);
      });
    }

    return Array.from(list).sort();
  }, [data]);

  // Client-side filtering & sorting
  const filteredVisitors = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];
    let list = [...data.data];

    // Filter by Designation
    if (designation) {
      const dQuery = designation.toLowerCase().trim();
      list = list.filter((v) => {
        const des = (v?.designation || "Visitor").toLowerCase();
        return des.includes(dQuery);
      });
    }

    // Filter by Product Category
    if (selectedCategories && selectedCategories.length > 0) {
      list = list.filter((v) => {
        const vCats = Array.isArray(v?.category)
          ? v.category
          : Array.isArray(v?.product_categories)
          ? v.product_categories
          : typeof v?.category === "string"
          ? [v.category]
          : [];

        if (vCats.length === 0) return true;

        return selectedCategories.some((cat) =>
          vCats.some((vc) => vc.toLowerCase().includes(cat.toLowerCase()))
        );
      });
    }

    // Filter by selected letter (A-Z)
    if (selectedLetter) {
      const sLetter = selectedLetter.toUpperCase();
      list = list.filter((v) => {
        const fLetter = (v.firstName || v.name || "").charAt(0).toUpperCase();
        return fLetter === sLetter;
      });
    }

    // Sort by name
    return list.sort((a, b) => {
      const nameA = `${a.firstName || a.name || ""} ${a.lastName || ""}`
        .trim()
        .toLowerCase();
      const nameB = `${b.firstName || b.name || ""} ${b.lastName || ""}`
        .trim()
        .toLowerCase();

      if (!nameA) return 1;
      if (!nameB) return -1;

      return sortOrder === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [data, designation, selectedCategories, selectedLetter, sortOrder]);

  // --- Handlers ---
  const onItemSearch = (e) => {
    setSq(e.target.value);
    setPage(1);
  };

  const handleCategoriesChange = (event, newValue) => {
    setSelectedCategories(newValue);
    setPage(1);
  };

  const handleLetterSelect = (newV) => {
    setSelectedLetter(newV || "");
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const onPageChange = (e, newPage) => {
    setPage(newPage);
  };

  const totalPages = data?.totalItems ? Math.ceil(data.totalItems / 30) : 0;
  const hasVisitors = filteredVisitors && filteredVisitors.length > 0;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <CustomCircularP show={isLoading} />

      <FilterDialogMui
        selectedCountry={selectedCountry}
        setSelectedCountry={(val) => {
          setSelectedCountry(val);
          setPage(1);
        }}
        selectedHall={selectedCategories.join(",")}
        setSelectedHall={(val) => {
          setSelectedCategories(val ? val.split(",") : []);
          setPage(1);
        }}
        selectedLetter={selectedLetter}
        setSelectedLetter={handleLetterSelect}
        open={openFilter}
        setOpen={setOpenFilter}
        catLabel="Filter by Category"
        categories={uniqueCategories}
      />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <PageHeader
          title={type === "td" ? "Trade Delegates" : "Visitors"}
          subtitle={
            type === "td"
              ? "Network with VIP trade delegates and verified buyers"
              : "Connect with registered trade visitors and industry participants"
          }
          icon={type === "td" ? BadgeIcon : PeopleIcon}
          count={data?.totalItems}
          countLabel={type === "td" ? "Delegates" : "Visitors"}
        />

        {/* Filter Bar Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eef2f6",
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            p: 3,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Filter Designation (Left) */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                size="medium"
                options={designationOptions}
                value={designation}
                onInputChange={(event, newInputValue) => {
                  setDesignation(newInputValue || "");
                  setPage(1);
                }}
                onChange={(event, newValue) => {
                  setDesignation(newValue || "");
                  setPage(1);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Designation"
                    placeholder="Filter by Designation..."
                  />
                )}
              />
            </Grid>

            {/* Product Category Filter (Middle) */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                multiple
                size="medium"
                options={uniqueCategories}
                value={selectedCategories}
                onChange={handleCategoriesChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Category"
                    placeholder="Select Categories..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      color="primary"
                      variant="outlined"
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
              />
            </Grid>

            {/* Search Visitors (Right) */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search Visitors by name, company..."
                variant="outlined"
                value={sq}
                onChange={onItemSearch}
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288d1" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Alphabet Sort & View Mode Toolbar */}
      <Box sx={{ mb: 3, width: "100%" }}>
        <AlphabetSortWidget
          selectedLetter={selectedLetter}
          onSelectLetter={handleLetterSelect}
          sortOrder={sortOrder}
          onSortToggle={handleSortToggle}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </Box>

      {/* Visitor Cards Content */}
      {!isLoading && !hasVisitors ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="30vh"
        >
          <Typography variant="h6" color="textSecondary">
            {type === "td"
              ? "No trade delegates found matching your criteria."
              : "No visitors found matching your criteria."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredVisitors.map((item, index) => (
            <Grid
              item
              xs={12}
              sm={viewMode === "grid" ? 6 : 12}
              md={viewMode === "grid" ? 4 : 12}
              lg={viewMode === "grid" ? 3 : 12}
              key={item._id || index}
            >
              <VisitorCard item={item} viewMode={viewMode} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box mt={4} mb={2} justifyContent="center" display="flex">
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default Visitors;
